import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import Input from 'views/ui/form_elements/input';
import { injectIntl, FormattedMessage, defineMessages } from 'react-intl';
import CountryDropdown from 'views/ui/form_elements/dropdown/country';
import classnames from 'classnames';
import StripeService from 'services/stripe';
import { StripeCardElement } from 'ui_system/inputs/stripe_card';
import BookingRequestActionCreator from 'action_creators/booking_request';

const FIELD_NAME_MESSAGES = defineMessages({
  cardNumber: { id: 'FIELD_NAME_CARD_NUMBER', defaultMessage: 'Card number' },
  cardExpiry: { id: 'FIELD_NAME_CARD_EXPIRY', defaultMessage: 'Card expiry' },
  cardCvc: { id: 'FIELD_NAME_CARD_CVC', defaultMessage: 'Card CVC' },
  postalCode: {
    id: 'FIELD_NAME_POSTAL_CODE',
    defaultMessage: 'Postal code',
  },
  cardholder_first_name: {
    id: 'FIELD_NAME_CARDHOLDER_FIRST_NAME',
    defaultMessage: "Cardholder''s first name",
  },
  cardholder_last_name: {
    id: 'FIELD_NAME_CARDHOLDER_LAST_NAME',
    defaultMessage: "Cardholder''s last name",
  },
  billing_address_line_1: {
    id: 'FIELD_NAME_BILLING_ADDRESS_LINE_1',
    defaultMessage: 'Billing address line 1',
  },
  billing_address_line_2: {
    id: 'FIELD_NAME_BILLING_ADDRESS_LINE_2',
    defaultMessage: 'Billing address line 2',
  },
  billing_city: {
    id: 'FIELD_NAME_BILLING_CITY',
    defaultMessage: 'Billing city',
  },
  billing_region: {
    id: 'FIELD_NAME_BILLING_REGION',
    defaultMessage: 'Billing region',
  },
  billing_country: {
    id: 'FIELD_NAME_BILLING_COUNTRY',
    defaultMessage: 'Billing country',
  },
});

class Payment extends React.Component {
  constructor(props) {
    super(props);
    StripeService._init(props.managedAccountToken, props.stripeTheme.fonts);
    this.postalCodeElement = null;
  }

  _prefilledPostalFromFields(fields) {
    return (
      fields.get('postalCode') ||
      fields.get('billing_zip_code') ||
      fields.get('zip_code')
    );
  }

  _applyPostalPrefill(element) {
    const v = this._prefilledPostalFromFields(this.props.fields);
    if (!v || !element || typeof element.update !== 'function') {
      return;
    }
    try {
      element.update({ value: String(v) });
    } catch (_e) {
      // Stripe.js version may not support programmatic postal value
    }
  }

  componentDidMount() {
    const { stripeTheme, intl } = this.props;
    // remove fonts from theme to avoid passing in invalid styles
    const { fonts, ...stripeStyle } = stripeTheme;
    const card = window.elements.create('cardNumber', {
      style: stripeStyle,
      disableLink: true,
    });
    const cardExpiry = window.elements.create('cardExpiry', {
      style: stripeStyle,
      placeholder: intl.formatMessage({
        id: 'MM_YY',
        defaultMessage: 'MM / YY',
      }),
    });
    const cardCvc = window.elements.create('cardCvc', {
      style: stripeStyle,
      placeholder: intl.formatMessage({
        id: 'CARD_CVC',
        defaultMessage: 'Card CVC',
      }),
    });
    const postalCodeElement = window.elements.create('postalCode', {
      style: stripeStyle,
      placeholder: intl.formatMessage({
        defaultMessage: 'Zip/Postal code',
      }),
    });

    BookingRequestActionCreator.addCardElement(card);
    card.mount('#stripe-card-element');
    card.addEventListener('change', this.formProps('cardNumber').onChange);
    cardExpiry.mount('#stripe-expiry-element');
    cardExpiry.addEventListener(
      'change',
      this.formProps('cardExpiry').onChange
    );
    cardCvc.mount('#stripe-cvc-element');
    cardCvc.addEventListener('change', this.formProps('cardCvc').onChange);

    this.postalCodeElement = postalCodeElement;
    postalCodeElement.mount('#stripe-postalCode-element');
    postalCodeElement.addEventListener(
      'change',
      this.formProps('postalCode').onChange
    );
    this._applyPostalPrefill(postalCodeElement);

    this.payment.addEventListener(
      'focus',
      () => this.props.googleTagManagerCheckoutProgress(3),
      {
        capture: true,
        once: true,
      }
    );
  }

  componentDidUpdate(prevProps) {
    if (!this.postalCodeElement) {
      return;
    }
    const prev = this._prefilledPostalFromFields(prevProps.fields);
    const cur = this._prefilledPostalFromFields(this.props.fields);
    if (cur && cur !== prev) {
      this._applyPostalPrefill(this.postalCodeElement);
    }
  }

  formProps(fieldName, otherProps) {
    const { errors, fields, onChange } = this.props;
    const translatedErrors =
      errors.get(fieldName) !== undefined
        ? this.translateError(fieldName)
        : errors.get(fieldName);

    return {
      ...otherProps,
      name: fieldName,
      value: fields.get(fieldName),
      errors: translatedErrors,
      onChange,
    };
  }

  translateError(fieldName) {
    const { errors, intl } = this.props;

    return errors.get(fieldName).map((error) => {
      const fieldMessage = FIELD_NAME_MESSAGES[fieldName];
      const translatedFieldName = fieldMessage
        ? intl.formatMessage(fieldMessage)
        : fieldName;
      return intl.formatMessage(
        {
          id: 'FIELD_CANT_BE_BLANK',
          defaultMessage: "{fieldName} can''t be blank",
        },
        { fieldName: translatedFieldName }
      );
    });
  }

  zipCodeLabel() {
    const { fields, intl } = this.props;
    return fields.get('billing_country') === 'US'
      ? intl.formatMessage({ id: 'ZIP_CODE', defaultMessage: 'Zip Code' })
      : intl.formatMessage({
          id: 'POSTAL_CODE',
          defaultMessage: 'Postal Code',
        });
  }

  stripeCardElementErrors() {
    const { errors } = this.props;
    return errors.get('stripe_card_element');
  }

  renderUsOnlyGuide() {
    const { guide_us_only: guideUSOnly } = this.props;

    if (guideUSOnly) {
      return (
        <span className="us-only-guide">
          We currently only accept online bookings from the United States.
        </span>
      );
    }
  }

  getErrorFor(str) {
    const { errors } = this.props;
    if ('get' in errors) {
      return errors.get(str);
    }
    return errors[str];
  }

  render() {
    const { disabled, errors, intl, guide_us_only: guideUSOnly } = this.props;
    const sectionClasses = classnames('CheckoutPayment', 'CheckoutSection', {
      'CheckoutSection-disabled': disabled,
    });

    const postalCodeClass = this.getErrorFor('postalCode')
      ? 'postal-error'
      : '';
    return (
      <div
        className={sectionClasses}
        ref={(c) => {
          this.payment = c;
        }}
      >
        <div className="row">
          <form autoComplete="off">
            <label htmlFor="cardHolderName" id="cardholder-name-field">
              <FormattedMessage
                id="CARDHOLDERS_NAME"
                defaultMessage="Cardholder''s Name"
              />
            </label>
            <div className="ui column grid">
              <div className="six wide column">
                <Input
                  {...this.formProps('cardholder_first_name', {
                    placeholder: intl.formatMessage({
                      id: 'FIRST',
                      defaultMessage: 'First',
                    }),
                    id: 'cardHolderName',
                    ariaLabelledBy: 'cardholder-name-field',
                    autoComplete: 'given-name',
                  })}
                />
              </div>
              <div className="six wide column">
                <Input
                  {...this.formProps('cardholder_last_name', {
                    placeholder: intl.formatMessage({
                      id: 'LAST',
                      defaultMessage: 'Last',
                    }),
                    ariaLabelledBy: 'cardholder-name-field',
                    autoComplete: 'family-name',
                  })}
                />
              </div>
            </div>
          </form>
        </div>
        <div className="row">
          <form autoComplete="off">
            <StripeCardElement
              errors={errors}
              onChange={this.formProps('stripe_card_element').onChange}
            />
          </form>
        </div>
        <div className="row">
          <form autoComplete="off">
            <label htmlFor="billingAddress" id="billing-address-field">
              <FormattedMessage
                id="BILLING_ADDRESS"
                defaultMessage="Billing Address"
              />
            </label>
            <div style={{ marginBottom: 10 }}>
              <Input
                {...this.formProps('billing_address_line_1', {
                  'data-testid': 'billing-addr1-input',
                  placeholder: intl.formatMessage({
                    id: 'ADDRESS_LINE_1',
                    defaultMessage: 'Address Line 1',
                  }),
                  id: 'billingAddress',
                  ariaLabelledBy: 'billing-address-field',
                  autoComplete: 'address-line1',
                })}
              />
            </div>
            <Input
              {...this.formProps('billing_address_line_2', {
                'data-testid': 'billing-addr2-input',
                placeholder: intl.formatMessage({
                  id: 'ADDRESS_LINE_2',
                  defaultMessage: 'Address Line 2',
                }),
                ariaLabelledBy: 'billing-address-field',
                autoComplete: 'address-line2',
              })}
            />
          </form>
        </div>
        <div className="row">
          <form autoComplete="off">
            <div className="ui column grid">
              <div className="six wide column">
                <label htmlFor="city" id="city-field">
                  <FormattedMessage id="CITY" defaultMessage="City" />
                </label>
                <Input
                  {...this.formProps('billing_city', {
                    'data-testid': 'city-input',
                    placeholder: intl.formatMessage({
                      id: 'CITY',
                      defaultMessage: 'City',
                    }),
                    id: 'city',
                    ariaLabelledBy: 'city-field',
                    autoComplete: 'address-level2',
                  })}
                />
              </div>
              <div className="six wide column">
                <label htmlFor="stateRegion" id="state-region-field">
                  {intl.formatMessage({
                    id: 'STATE_REGION',
                    defaultMessage: 'State/Region',
                  })}
                </label>
                <Input
                  {...this.formProps('billing_region', {
                    'data-testid': 'state-input',
                    placeholder: intl.formatMessage({
                      id: 'STATE',
                      defaultMessage: 'State',
                    }),
                    id: 'stateRegion',
                    ariaLabelledBy: 'state-region-field',
                    autoComplete: 'address-level1',
                  })}
                />
              </div>
            </div>
          </form>
        </div>
        <div className="row country">
          <div className="ui column grid">
            <div className="six wide column">
              <form autoComplete="off" id="country-field">
                <label aria-label="Select country">
                  <FormattedMessage id="COUNTRY" defaultMessage="Country" />
                </label>
                <CountryDropdown
                  {...this.formProps('billing_country', {
                    'data-testid': 'country-input',
                    guide_us_only: guideUSOnly,
                    id: 'country',
                    ariaLabelledBy: 'country-field',
                  })}
                />
              </form>
            </div>
            <div className="six wide column">
              <form
                autoComplete="off"
                onSubmit={(e) => e.preventDefault()}
                className={postalCodeClass}
              >
                <label>{this.zipCodeLabel()}</label>
                <div
                  className={
                    this.getErrorFor('postalCode')
                      ? 'postal-element error'
                      : 'postal-element '
                  }
                  id="stripe-postalCode-element"
                  data-testid="zip-code-input"
                />
                <span className="input-error">
                  {this.getErrorFor('postalCode') || ''}
                </span>
              </form>
            </div>
          </div>
          {this.renderUsOnlyGuide()}
        </div>
      </div>
    );
  }
}

Payment.propTypes = {
  fields: PropTypes.object,
  errors: PropTypes.object,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  managedAccountToken: PropTypes.string,
  stripeTheme: PropTypes.object,
  stepNo: PropTypes.number,
  googleTagManagerCheckoutProgress: PropTypes.func,
  intl: PropTypes.object.isRequired,
};

export default injectIntl(Payment);
